import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    globalTags: [],
    selectedData: [],
    fetchStatus: "success",
    fetchGlobalTagsStatus: "success",
    assignTagStatus: "idle",
    unassignedTagStatus: "idle",
    fetchByIdStatus: "idle",
    createStatus: "idle",
    editStatus: "idle",
    updateStatus: "idle",
    deleteStatus: "idle",
    pagination: {},
    error: null
};

export const fetchGlobalTags = createAsyncThunk('tag/fetchGlobalTags', async ({scopeId, scope}) => {
    try {
        let response;
        if (scope.toLowerCase() === "restaurant") {
            response = await api.get(`/adg/list/global/tags?id=${scopeId}`);
        } else {
            response = await api.get(`/adg/list/restaurant/tags?id=${scopeId}`);
        }
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchTags = createAsyncThunk('tag/fetchTags', async ({roleSign, scopeId, scope, page, limit}) => {
    try {
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);

        const queryString = params.toString();
        const fullParams = queryString ? `&${queryString}` : '';
        const response = await api.get(`/${roleSign}/${scope.toLowerCase()}tag?id=${scopeId}${fullParams}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const createRestaurantTag = createAsyncThunk('tag/createRestaurantTag', async ({roleSign, scopeId, data}) => {
    try {
        const response = await api.post(`/${roleSign}/restauranttag/addNew`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const editTag = createAsyncThunk('tag/editTag', async ({scope, id, data}) => {
    try {
        const response = await api.patch(`/adg/${scope.toLowerCase()}tag/${id}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const deleteTag = createAsyncThunk('tag/deleteTag', async (id) => {
    try {
        const response = await api.delete(`/adg/restauranttag/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const updateTag = createAsyncThunk('tag/updateTag', async ({scope, scopeId, tag}) => {
    const data = {
        id: scopeId,
        tag
    };
    try {
        const response = await api.post(`/adg/${scope.toLowerCase()}tag`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const AssignTagToProducts = createAsyncThunk('tag/AssignTagToProducts', async ({roleSign, scopeId, data}) => {
    try {
        const response = await api.post(`/${roleSign}/restauranttag/updateProducts`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const UnassignedTagFromProducts = createAsyncThunk('tag/UnassignedTagFromProducts', async ({roleSign, scopeId, data}) => {
    try {
        const response = await api.patch(`/${roleSign}/restauranttag/remove`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchTagById = createAsyncThunk('tag/fetchTagById', async ({scope, id}) => {
    try {
        const response = await api.get(`/adg/${scope.toLowerCase()}tag/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

const tagSlice = createSlice({
    name: "tag",
    initialState,
    reducers: {
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetEditStatus(state) {
            state.editStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetAssignTagStatus(state) {
            state.assignTagStatus = 'idle';
        },
        resetUnassignedTagStatus(state) {
            state.unassignedTagStatus = 'idle';
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchGlobalTags.pending, (state) => {
                state.fetchGlobalTagsStatus = 'loading';
            })
            .addCase(fetchGlobalTags.fulfilled, (state, action) => {
                const {fetchGlobalTagsStatus, error} = handleResponseStatus(action.payload, 'fetchGlobalTagsStatus');
                state.globalTags = fetchGlobalTagsStatus === "success" ? updateIdField(action.payload.data.data) : state.globalTags;
                state.selectedData = fetchGlobalTagsStatus === "success" ? action.payload.data.selectedData : state.selectedData;
                state.fetchGlobalTagsStatus = fetchGlobalTagsStatus;
                state.error = error;
            })
            .addCase(fetchGlobalTags.rejected, (state, action) => {
                state.fetchGlobalTagsStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(fetchTags.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchTags.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchTags.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(fetchTagById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchTagById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchTagById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            //Create tag for restaurant
            .addCase(createRestaurantTag.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createRestaurantTag.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createRestaurantTag.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            //Assign tag to products
            .addCase(AssignTagToProducts.pending, (state) => {
                state.assignTagStatus = 'loading';
            })
            .addCase(AssignTagToProducts.fulfilled, (state, action) => {
                const {assignTagStatus, error} = handleResponseStatus(action.payload, 'assignTagStatus');
                state.assignTagStatus = assignTagStatus;
                state.error = error;
            })
            .addCase(AssignTagToProducts.rejected, (state, action) => {
                state.assignTagStatus = 'failed';
                state.error = action.error.message;
            })

            //Unassigned tag from products
            .addCase(UnassignedTagFromProducts.pending, (state) => {
                state.unassignedTagStatus = 'loading';
            })
            .addCase(UnassignedTagFromProducts.fulfilled, (state, action) => {
                const {unassignedTagStatus, error} = handleResponseStatus(action.payload, 'unassignedTagStatus');
                state.unassignedTagStatus = unassignedTagStatus;
                state.error = error;
            })
            .addCase(UnassignedTagFromProducts.rejected, (state, action) => {
                state.unassignedTagStatus = 'failed';
                state.error = action.error.message;
            })

            .addCase(editTag.pending, (state) => {
                state.editStatus = 'loading';
            })
            .addCase(editTag.fulfilled, (state, action) => {
                const {editStatus, error} = handleResponseStatus(action.payload, 'editStatus');
                state.editStatus = editStatus;
                state.error = error;
            })
            .addCase(editTag.rejected, (state, action) => {
                state.editStatus = 'failed';
                state.error = action.error.message;
            })

            .addCase(updateTag.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateTag.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateTag.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(deleteTag.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteTag.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                state.deleteStatus = deleteStatus;
                state.items = state.items.filter((item) => item.id !== action.payload.data.data._id);
                state.error = error;
            })
            .addCase(deleteTag.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetUpdateStatus,
    resetAssignTagStatus,
    resetUnassignedTagStatus,
    resetCreateStatus,
    resetDeleteStatus,
    resetEditStatus
} = tagSlice.actions;
export default tagSlice.reducer;
